import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  FormControl,
  Select,
} from '@mui/material';
import { ArrowDropDown, KeyboardArrowDown } from '@mui/icons-material';
import { useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
import { ThemePalette } from 'mui.theme';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { SWTextField } from 'components/Form/Input/SWTextField';
import { AlertItem } from './Item';
import SquaresAlertHandler from 'handlers/squaresHandlers/squaresAlerts.handler';
import type { AlertFeedbackPayload } from 'types/squares.type';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const AlertsView = () => {
  const { t } = useTranslation();
  const alertHandler = new SquaresAlertHandler();
  const themeState = useAppSelector((state) => state.authReducer).customer
    .theme;
  const enableDarkTheme = isDarkTheme(themeState);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [searched, setSearched] = useState<{
    asset: string;
  }>({ asset: '' });
  const [selectedFormat, setSelectedFormat] = useState('.xls');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sortType, setSortType] = useState<string>('severity');
  const { assets, isLoadingAllAssets } = useAppSelector(
    (state) => state.assetReducer
  );
  const { notifications, isLoading } = useAppSelector(
    (state) => state.squaresAlertReducer
  );
  const customerId = useAppSelector((state) => state.authReducer)?.customer?.id;
  const open = Boolean(anchorEl);

  const handleOpen = (e: any) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleSelectFormat = (format: string) => {
    setSelectedFormat(format);
    handleClose();
  };

  const fontColor = palette.textPrimary;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setSearched({
      ...searched,
      [name]: value,
    });
  };

  const updateSort = (event: any) => {
    setSortType(event.target.value);
  };

  const filteredData = useMemo(() => {
    if (!searched.asset || searched.asset === '') return notifications;

    const lowerSearch = searched.asset.toLowerCase();

    return notifications.filter(
      (item) =>
        item.assetName.toLowerCase().includes(lowerSearch) ||
        item.makeModel.toLowerCase().includes(lowerSearch)
    );
  }, [searched, notifications]);

  useEffect(() => {
    void alertHandler.getAllAlerts(customerId);
  }, []);

  const acknowledgeNotification = async (notificationId: string) => {
    await alertHandler.acknowledgeAlert(notificationId);
  };

  const submitFeedback = async (
    notificationId: string,
    userFeedback: AlertFeedbackPayload
  ) => {
    await alertHandler.submitAlertFeedback(notificationId, userFeedback);
  };

  return (
    <>
      {isLoading || isLoadingAllAssets ? (
        <SingleFadeLoader />
      ) : (
        <Box
          sx={{
            marginBottom: '20px',
          }}
        >
          {notifications.length === 0 ? (
            <Typography
              sx={{
                color: fontColor,
                fontSize: '14px',
                marginTop: '20px',
              }}
            >
              {t('no_alerts_available')}
            </Typography>
          ) : (
            <>
              <Box
                sx={{
                  marginTop: '10px',
                  background: enableDarkTheme
                    ? palette.boxBackground
                    : ThemePalette.light.topBar,
                  padding: '10px',
                  borderTopRightRadius: '4px',
                  borderTopLeftRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: fontColor,
                      marginRight: '20px',
                    }}
                  >
                    {notifications.length} {t('alerts_total')}
                  </Typography>
                  <Box
                    key="asset"
                    sx={{
                      marginRight: '20px',
                    }}
                  >
                    <SWTextField
                      key="asset"
                      name="asset"
                      placeholder={t('search_for_an_asset')}
                      enableDarkTheme={enableDarkTheme}
                      TextFieldProps={{
                        name: 'asset',
                        value: searched.asset,
                        onChange: handleChange,
                        InputProps: {
                          sx: {
                            height: '40px',
                            minWidth: '150px',
                            color: fontColor,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: enableDarkTheme
                                ? 'rgba(255, 255, 255, 0.2)'
                                : 'rgba(0, 0, 0, 0.15)',
                              transition: 'border-color 0.2s ease',
                            },
                          },
                        },
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: fontColor,
                      marginRight: '10px',
                    }}
                  >
                    {t('sort_by_10')}
                  </Typography>

                  <FormControl sx={{ minWidth: 120 }}>
                    <Select
                      value={sortType}
                      onChange={updateSort}
                      displayEmpty
                      IconComponent={KeyboardArrowDown}
                      sx={{
                        height: '40px',
                        width: '206px',
                        fontSize: '14px',
                        color: fontColor,
                        border: '1px solid #26364C',
                        borderColor: enableDarkTheme
                          ? 'rgba(255, 255, 255, 0.2)'
                          : 'rgba(0, 0, 0, 0.15)',
                        '& .MuiSelect-select': {
                          paddingLeft: '12px',
                          paddingRight: '32px !important',
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#9ca3af',
                          right: '8px',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: enableDarkTheme
                              ? palette.boxBackground
                              : palette.boxBackground,
                            color: fontColor,
                          },
                        },
                      }}
                    >
                      <MenuItem
                        value="severity"
                        sx={{
                          fontSize: '14px',
                        }}
                      >
                        {t('severity')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <>
                    <ButtonGroup
                      variant="contained"
                      sx={{
                        borderRadius: '4px',
                        backgroundColor: '#4160ff',
                      }}
                    >
                      <Button
                        sx={{
                          backgroundColor: '#4160ff',
                          textTransform: 'none',
                          padding: '5px 20px',
                          '&:hover': { backgroundColor: '#3754e6' },
                        }}
                      >
                        Export
                      </Button>

                      <Button
                        onClick={handleOpen}
                        sx={{
                          backgroundColor: '#3754e6',
                          minWidth: '60px',
                          textTransform: 'none',
                          fontSize: '16px',
                          '&:hover': { backgroundColor: '#3049cc' },
                        }}
                        endIcon={<ArrowDropDown />}
                      >
                        {selectedFormat}
                      </Button>
                    </ButtonGroup>

                    <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                      <MenuItem
                        onClick={() => {
                          handleSelectFormat('.xls');
                        }}
                      >
                        .xls
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleSelectFormat('.csv');
                        }}
                      >
                        .csv
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleSelectFormat('.pdf');
                        }}
                      >
                        .pdf
                      </MenuItem>
                    </Menu>
                  </>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  bgcolor: enableDarkTheme
                    ? palette.toolBarBackground
                    : palette.toolBarBackground,
                  padding: '10px',
                  borderBottom: '1px solid #1e3a5f',
                  borderBottomRightRadius: '4px',
                  borderBottomLeftRadius: '4px',
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    color: '#94a3b8',
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {t('notification_type')}
                </Typography>
                <Typography
                  sx={{
                    flex: 0.8,
                    color: '#94a3b8',
                    fontSize: 14,
                    fontWeight: 500,
                    marginLeft: '10px',
                  }}
                >
                  {t('asset_id')}
                </Typography>
                <Typography
                  sx={{
                    flex: 0.8,
                    color: '#94a3b8',
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {t('notification_date')}
                </Typography>
                <Typography
                  sx={{
                    flex: 1.5,
                    color: '#94a3b8',
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {t('detailed_message')}
                </Typography>
                <Typography
                  sx={{
                    flex: 0.5,
                    color: '#94a3b8',
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {t('severity')}
                </Typography>
                <Box sx={{ width: 48 }} />
              </Box>
              {filteredData.map((notification, index) => {
                return (
                  <AlertItem
                    key={index}
                    enableDarkTheme={enableDarkTheme}
                    notification={notification}
                    acknowledgeNotification={acknowledgeNotification}
                    assets={assets}
                    submitFeedback={submitFeedback}
                  />
                );
              })}
            </>
          )}
        </Box>
      )}
    </>
  );
};
